import { HERO_SUN_COLOR, heroSunGlowBoxShadow } from '@/components/hero-sun-disc'
import { mixHexColors, readCssColor, readResolvedCssColor } from '@/lib/color'

/** First quarter: grow while staying a semicircle. */
export const BIG_SUN_CIRCLE_PHASE_END = 0.25

/** Settled big-sun fill in dark mode — ocean sibling (matches `--morning-ember-deep`). */
const DARK_SETTLED_FILL_FALLBACK = '#1b3942'

function easeOutCubic(t: number) {
  return 1 - (1 - t) ** 3
}

function lerp(from: number, to: number, t: number) {
  return from + (to - from) * t
}

export type BigSunBounds = {
  sectionW: number
  sectionH: number
  vw: number
}

export type BigSunFrame = {
  width: number
  height: number
  /** Top-left / top-right radius; bottom corners stay 0 (semicircle → flat band). */
  borderRadius: number
  /** 0 = hero orange + glow; 1 = settled ember, glow gone. */
  fillPhaseT: number
}

/** Match Tailwind `lg` — mobile + tablet grow edge-to-edge. */
const BIG_SUN_FULL_WIDTH_MAX_VW = 1024

/**
 * Pure geometry for the process-section big-sun:
 * 1. 0–25% — grow as a true semicircle (width = 2 × height)
 * 2. 25–100% — expand to full width, flatten, and shift fill toward ember
 *
 * On mobile and tablet, phase 1 already reaches full section width so the
 * scroll-in reads edge-to-edge; desktop keeps an inset mid-size before expanding.
 */
export function computeBigSunFrame(
  progress: number,
  { sectionW, sectionH, vw }: BigSunBounds,
): BigSunFrame {
  const startW = Math.min(vw * 0.4, 20 * 16)
  const startH = startW / 2

  const midW =
    vw < BIG_SUN_FULL_WIDTH_MAX_VW
      ? sectionW
      : Math.min(vw * 0.72, sectionW * 0.72)
  const midH = midW / 2

  if (progress <= BIG_SUN_CIRCLE_PHASE_END) {
    const t = easeOutCubic(progress / BIG_SUN_CIRCLE_PHASE_END)
    const width = lerp(startW, midW, t)
    const height = lerp(startH, midH, t)
    return {
      width,
      height,
      borderRadius: height,
      fillPhaseT: 0,
    }
  }

  const t = easeOutCubic(
    (progress - BIG_SUN_CIRCLE_PHASE_END) / (1 - BIG_SUN_CIRCLE_PHASE_END),
  )
  const width = lerp(midW, sectionW, t)
  const height = lerp(midH, sectionH, t)
  const borderRadius = lerp(midH, 0, t)
  return {
    width,
    height,
    borderRadius,
    fillPhaseT: t,
  }
}

/**
 * Overlay open geometry (rises from the bottom):
 * 1. 0–55% — true semicircle grows until its crest meets the top of the frame
 * 2. 55–100% — reshape to full viewport width and flatten; fill shifts to ember
 */
export const OVERLAY_SUN_CIRCLE_PHASE_END = 0.55

export function computeOverlayBigSunFrame(
  progress: number,
  { sectionW, sectionH, vw }: BigSunBounds,
): BigSunFrame {
  const startW = Math.min(vw * 0.4, 20 * 16)
  const startH = startW / 2

  // True semicircle whose height fills the frame (sides may overflow).
  const midH = sectionH
  const midW = midH * 2

  if (progress <= OVERLAY_SUN_CIRCLE_PHASE_END) {
    const t = easeOutCubic(progress / OVERLAY_SUN_CIRCLE_PHASE_END)
    const width = lerp(startW, midW, t)
    const height = lerp(startH, midH, t)
    return {
      width,
      height,
      borderRadius: height,
      fillPhaseT: 0,
    }
  }

  const t = easeOutCubic(
    (progress - OVERLAY_SUN_CIRCLE_PHASE_END) /
      (1 - OVERLAY_SUN_CIRCLE_PHASE_END),
  )
  const width = lerp(midW, sectionW, t)
  const borderRadius = lerp(midH, 0, t)
  return {
    width,
    height: sectionH,
    borderRadius,
    fillPhaseT: t,
  }
}

export function isDocumentDark() {
  return document.documentElement.classList.contains('dark')
}

export function getBigSunSettledFillColor(): string {
  return isDocumentDark()
    ? readResolvedCssColor('--morning-ember-deep', DARK_SETTLED_FILL_FALLBACK)
    : readCssColor('--morning-ember', '#c2540a')
}

/** Fill color + glow for a given fillPhaseT (0 = hero orange, 1 = settled). */
export function bigSunFillStyles(fillPhaseT: number): {
  backgroundColor: string
  boxShadow: string
} {
  const backgroundColor = mixHexColors(
    HERO_SUN_COLOR,
    getBigSunSettledFillColor(),
    fillPhaseT,
  )
  const glow = isDocumentDark() ? 0 : 1 - fillPhaseT
  return {
    backgroundColor,
    boxShadow: heroSunGlowBoxShadow(glow),
  }
}

export function applyBigSunFrameStyles(
  sun: HTMLElement,
  fill: HTMLElement,
  frame: BigSunFrame,
) {
  sun.style.width = `${frame.width}px`
  sun.style.height = `${frame.height}px`
  sun.style.borderRadius = `${frame.borderRadius}px ${frame.borderRadius}px 0 0`
  const fillStyles = bigSunFillStyles(frame.fillPhaseT)
  fill.style.backgroundColor = fillStyles.backgroundColor
  fill.style.boxShadow = fillStyles.boxShadow
}

export function clearBigSunFrameStyles(sun: HTMLElement, fill: HTMLElement) {
  sun.style.removeProperty('width')
  sun.style.removeProperty('height')
  sun.style.removeProperty('border-radius')
  fill.style.removeProperty('background-color')
  fill.style.removeProperty('box-shadow')
}
